import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  EPageLayout,
  ESeverity,
  IToastService,
  IconComponent,
  PageLayoutComponent,
  toastServiceInjectionToken
} from "@zaeper/angular-dashboard-view-lib";
import { ZTemplateDirective } from "@zaeper/angular-core-lib";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { TagModule } from "primeng/tag";
import { MenuItem } from "primeng/api";
import { IConnectedAccount } from "../../models/i-connected-account";
import { IDeveloperBillingRestClient } from "../../rest-clients/billing/i-developer-billing-rest.client";
import { developerBillingRestClientInjectionToken } from "../../rest-clients/billing/developer-billing-rest-client-injection-token";

@Component({
  selector: "app-connect-onboarding-page",
  templateUrl: "./connect-onboarding-page.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    CardModule,
    TagModule,
    ZTemplateDirective,
    PageLayoutComponent,
    IconComponent
  ]
})
export class ConnectOnboardingPageComponent implements OnInit {
  public readonly ePageLayout: typeof EPageLayout = EPageLayout;

  public readonly navigation: MenuItem[] = [
    {
      icon: "pi pi-home",
      routerLink: "/de/"
    },
    {
      label: "Verkäufer werden",
      routerLink: "/de/connect"
    }
  ];

  public account: IConnectedAccount | null = null;
  public isLoading: boolean = true;
  public isOnboarding: boolean = false;

  constructor(
    @Inject(developerBillingRestClientInjectionToken) private readonly _billingRestClient: IDeveloperBillingRestClient,
    @Inject(toastServiceInjectionToken) private readonly _toastService: IToastService,
    private readonly _cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this._loadConnectStatus();
  }

  public get isOnboarded(): boolean {
    return this.account?.chargesEnabled === true && this.account?.payoutsEnabled === true;
  }

  public getOnboardingStatusLabel(status: string): string {
    switch (status) {
      case "NOT_STARTED":
        return "Nicht gestartet";
      case "PENDING":
        return "Ausstehend";
      case "COMPLETE":
        return "Abgeschlossen";
      default:
        return status;
    }
  }

  public getOnboardingStatusSeverity(status: string): "success" | "warn" | "danger" {
    switch (status) {
      case "COMPLETE":
        return "success";
      case "PENDING":
        return "warn";
      default:
        return "danger";
    }
  }

  public startOnboarding(): void {
    this.isOnboarding = true;
    this._cdr.markForCheck();

    this._billingRestClient.onboardConnect({
      refreshUrl: window.location.href,
      returnUrl: window.location.href
    }).then((link) => {
      window.location.href = link.url;
    }).catch(() => {
      this._toastService.setMessage({
        title: "Fehler",
        detail: "Onboarding konnte nicht gestartet werden",
        severity: ESeverity.error
      });
      this.isOnboarding = false;
      this._cdr.markForCheck();
    });
  }

  public openDashboard(): void {
    this._billingRestClient.getConnectDashboardLink().then((link) => {
      window.open(link.url, "_blank");
    }).catch(() => {
      this._toastService.setMessage({
        title: "Fehler",
        detail: "Dashboard-Link konnte nicht geladen werden",
        severity: ESeverity.error
      });
    });
  }

  private _loadConnectStatus(): void {
    this.isLoading = true;
    this._cdr.markForCheck();

    this._billingRestClient.getConnectStatus().then((account) => {
      this.account = account;
      this.isLoading = false;
      this._cdr.markForCheck();
    }).catch(() => {
      this.account = null;
      this.isLoading = false;
      this._cdr.markForCheck();
    });
  }
}
